import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { MoveLeft, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useOnboardingStatus } from '@/contexts/OnboardingStatusContext';

interface OnboardingStep {
    id: string;
    title: string;
    route: string;
}

interface OnboardingWrapperProps {
    children: React.ReactNode;
    currentStep: number;
    totalSteps: number;
    steps: OnboardingStep[];
    onNext?: () => Promise<boolean>;
    isSaving?: boolean;
    isLoading?: boolean;
    saveButtonText?: string;
    hideSteps?: boolean;
    hideNavigation?: boolean;
    accountType?: 'company' | 'architect' | 'subcontractor';
    completedSteps?: number[];
    secondaryButton?: {
        text: string;
        onClick: () => void;
        variant?: 'outline' | 'ghost' | 'secondary';
        disabled?: boolean;
    };
}

export default function OnboardingWrapper({
    children,
    currentStep,
    totalSteps,
    steps,
    onNext,
    isSaving = false,
    isLoading = false,
    saveButtonText = 'Save & Continue',
    hideSteps = false,
    hideNavigation = false,
    completedSteps = [],
    secondaryButton,
}: OnboardingWrapperProps) {
    const navigate = useNavigate();

    // Use centralized onboarding status context - SINGLE SOURCE OF TRUTH
    const {
        isLoading: isLoadingStatus,
        overallProgress,
        isStepCompleted,
        markStepCompleted,
        accountType: contextAccountType,
        organizationId
    } = useOnboardingStatus();

    // Handle externally provided completed steps
    useEffect(() => {
        if (completedSteps.length > 0) {
            completedSteps.forEach(step => markStepCompleted(step));
        }
    }, [completedSteps, markStepCompleted]);

    // Ensure steps before current are marked as completed
    useEffect(() => {
        for (let i = 1; i < currentStep; i++) {
            if (!isStepCompleted(i)) {
                markStepCompleted(i);
            }
        }
    }, [currentStep, isStepCompleted, markStepCompleted]);

    // Use progress from context - SINGLE SOURCE OF TRUTH
    const progressPercentage = overallProgress;

    // Get step status using context - treat optional steps as normal pending/completed
    const getStepStatus = (stepNumber: number) => {
        if (isStepCompleted(stepNumber)) {
            return 'completed';
        }
        if (stepNumber === currentStep) {
            return 'current';
        }
        return 'pending';
    };

    // Get status label for display
    const getStatusLabel = (stepNumber: number) => {
        const status = getStepStatus(stepNumber);
        switch (status) {
            case 'completed':
                return 'Complete';
            case 'current':
                return 'In Progress';
            case 'pending':
                return 'Pending';
            default:
                return 'Pending';
        }
    };

    // Navigation handlers
    const handleBack = useCallback(() => {
        const previousStep = steps[currentStep - 2];

        if (previousStep && organizationId) {
            const baseUrl = contextAccountType !== 'company' ? '/onboarding' : '/onboarding';
            const navigateUrl = `${baseUrl}/${previousStep.route}?orgId=${organizationId}&type=${contextAccountType}`;
            navigate(navigateUrl);
        } else if (previousStep) {
            const navigateUrl = `/onboarding/${previousStep.route}?type=${contextAccountType}`;
            navigate(navigateUrl);
        }
    }, [currentStep, navigate, organizationId, contextAccountType, steps]);

    const handleNext = useCallback(async () => {
        if (onNext) {
            try {
                const isCompleted = await onNext();

                if (isCompleted) {
                    // Mark current step as completed using context
                    markStepCompleted(currentStep);

                    // Navigate to next step
                    const nextStep = steps[currentStep];

                    if (nextStep && organizationId) {
                        const baseUrl = contextAccountType !== 'company' ? '/onboarding' : '/onboarding';
                        const navigateUrl = `${baseUrl}/${nextStep.route}?orgId=${organizationId}&type=${contextAccountType}`;
                        navigate(navigateUrl);
                    } else if (nextStep) {
                        const navigateUrl = `/onboarding/${nextStep.route}?type=${contextAccountType}`;
                        navigate(navigateUrl);
                    }
                }
            } catch (error) {
                console.error('OnboardingWrapper: Save failed:', error);
                toast({
                    title: 'Save Failed',
                    description: 'There was an error saving your progress. Please try again.',
                    variant: 'destructive',
                });
            }
        }
    }, [onNext, currentStep, navigate, organizationId, contextAccountType, steps, markStepCompleted]);

    // Show loading state while checking onboarding status
    if (isLoadingStatus && organizationId) {
        return (
            <div className="min-h-screen bg-muted flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Checking your progress...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-muted flex items-center justify-center p-4 md:p-12">
            <div className="w-full max-w-7xl">
                {/* Logo Section */}
                <div className="text-center mb-8">
                    <div className="mb-6">
                        <img
                            src="/logo.png"
                            alt="LarkPM Logo"
                            className="h-12 w-auto object-contain mx-auto"
                        />
                    </div>

                    {/* Title and Subtitle */}
                    <div className="mb-8">
                        <h1 className="text-4xl font-bold text-primary mb-2">
                            Welcome to LarkPM
                        </h1>
                        <p className="text-muted-foreground text-lg">
                            {contextAccountType === 'company'
                                ? "Let's get your company set up. This will only take a few minutes."
                                : `Complete your company onboarding to start partnering with our CRM platform.`
                            }
                        </p>
                    </div>
                </div>

                {/* Main Form Card */}
                <Card className="border-border">
                    <CardContent className="p-0">
                        {/* Header Section */}
                        {!hideSteps && (
                            <div className="px-4 md:px-8 pt-8">
                                {/* Step Title and Progress */}
                                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
                                    <h2 className="text-xl md:text-2xl font-bold tracking-tight text-foreground">
                                        Step {currentStep} of {totalSteps}: {steps[currentStep - 1]?.title}
                                    </h2>
                                    <span className="text-sm md:text-md font-medium text-muted-foreground">
                                        {Math.round(progressPercentage)}% Complete
                                    </span>
                                </div>

                                {/* Progress Bar */}
                                <div className="mb-6">
                                    <Progress value={progressPercentage} className="h-2" />
                                </div>

                                {/* Step Indicators - Responsive layout */}
                                <div className="grid grid-cols-2 md:flex md:justify-between md:items-center gap-2 md:gap-6 mb-6">
                                    {steps.map((step, index) => {
                                        const stepNumber = index + 1;
                                        const status = getStepStatus(stepNumber);
                                        const statusLabel = getStatusLabel(stepNumber);

                                        return (
                                            <div
                                                key={step.id}
                                                className="flex items-center gap-2 md:gap-3 min-w-0"
                                            >
                                                {/* Step Circle */}
                                                <div
                                                    className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center text-xs md:text-sm font-medium flex-shrink-0 ${status === 'completed'
                                                        ? 'bg-green-500 text-primary-foreground'
                                                        : status === 'current'
                                                            ? 'bg-yellow-500 text-foreground'
                                                            : 'bg-muted text-foreground-soft dark:bg-foreground-soft dark:text-muted-foreground'
                                                        }`}
                                                >
                                                    {status === 'completed' ? (
                                                        <Check className="w-3 h-3 md:w-4 md:h-4" />
                                                    ) : (
                                                        stepNumber
                                                    )}
                                                </div>

                                                {/* Step Details - Title and Status */}
                                                <div className="min-w-0 flex-1">
                                                    {/* Step Title - Truncated on mobile */}
                                                    <div
                                                        className={`text-xs md:text-sm font-medium truncate ${status === 'completed'
                                                            ? 'text-green-600'
                                                            : status === 'current'
                                                                ? 'text-yellow-600'
                                                                : 'text-muted-foreground'
                                                            }`}
                                                        title={step.title}
                                                    >
                                                        {step.title}
                                                    </div>
                                                    {/* Status Label */}
                                                    <div
                                                        className={`text-xs font-medium ${status === 'completed'
                                                            ? 'text-green-500'
                                                            : status === 'current'
                                                                ? 'text-yellow-500'
                                                                : 'text-muted-foreground/70'
                                                            }`}
                                                    >
                                                        {statusLabel}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Form Content */}
                        <div className="p-4 md:p-8">
                            {children}

                            {/* Navigation Buttons */}
                            {!hideNavigation && (
                                <div className="flex flex-col md:flex-row justify-between pt-6 mt-6 border-t border-border gap-4">
                                    <Button
                                        type="button"
                                        onClick={handleBack}
                                        variant="outline"
                                        disabled={currentStep === 1 || isSaving || isLoading}
                                        className="border-border flex items-center gap-2 disabled:opacity-50 w-full md:w-auto"
                                    >
                                        <MoveLeft className="w-4 h-4" />
                                        Back
                                    </Button>

                                    <div className="flex flex-col md:flex-row gap-3">
                                        {secondaryButton && (
                                            <Button
                                                type="button"
                                                onClick={secondaryButton.onClick}
                                                variant={secondaryButton.variant || 'outline'}
                                                disabled={secondaryButton.disabled || isSaving || isLoading}
                                                className="w-full md:w-auto"
                                            >
                                                {secondaryButton.text}
                                            </Button>
                                        )}
                                        <Button
                                            type="button"
                                            onClick={handleNext}
                                            disabled={isSaving || isLoading}
                                            className={`flex items-center gap-2 disabled:opacity-50 w-full md:w-auto ${currentStep === totalSteps
                                                ? 'bg-yellow-500 hover:bg-yellow-600 text-foreground'
                                                : 'bg-foreground-soft hover:bg-foreground text-primary-foreground'
                                                }`}
                                        >
                                            {isSaving || isLoading ? (
                                                <>
                                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Saving...
                                                </>
                                            ) : (
                                                <>
                                                    {currentStep === totalSteps ? 'Complete Setup' : saveButtonText}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
